// Today: the front page. The edition's headline, then one card: its gold letters, today's board
// beside yesterday's best (the solver's and the day's #1), and your next step (Play, Continue, or
// your result with the time to the next edition). Challenges get one line under the card, only
// when something needs you; the rest of them live on Rivals. Below, today's Slow Burn.
import { useEffect, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { headlineLetters, type EditionInfo } from '@wbwl/contract'
import { isBurn } from '@wbwl/rules'
import { MiniGrid, Tile } from '../components/game-ui.tsx'
import { HowTo } from '../components/HowTo.tsx'
import { Icon } from '../components/Icon.tsx'
import { TodayStrip } from '../components/rivals-ui.tsx'
import { DepthTrack, SlowBurnMark } from '../components/slow-burn-ui.tsx'
import { Avatar, ErrorNote, Loading, Logo, TabBar, useToast } from '../components/shell.tsx'
import { api } from '../lib/api.ts'
import { useDayOver, useEdition, useMe, useMyChallenges, usePlay, useRun, useSlowBurn } from '../lib/data.ts'
import { fmt, goldByTier, longDate, takeReturnAfterSignIn } from '../lib/game.ts'
import { useIsDesktop } from '../lib/layout.ts'
import { dayOf } from '../lib/rivals.ts'
import { invalidate } from '../lib/store.ts'

export default function TodayPage() {
  const ed = useEdition('today')
  const info = ed.data
  const play = usePlay(info?.number ?? null).data
  const me = useMe().data
  const days = useMyChallenges().data?.editions.map(dayOf) ?? []
  const [howto, setHowto] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const number = info?.number
  const dayOver = useDayOver(info?.nextAt)

  useEffect(() => {
    if (!dayOver || number === undefined) return
    // Midnight Pacific passed with the page open: load the new edition, and everything that turns
    // over with it (this one becomes yesterday's, with its best words). Until the server has the new
    // one, ask again each minute.
    invalidate('edition:today', `edition:${number}`, 'play:', 'me', 'stats', 'board:', 'edchallenges:', 'mychallenges', 'sbinfo:', 'sbrun:', 'sbboard:')
    const retry = setInterval(() => invalidate('edition:today', 'sbinfo:today'), 60_000)
    return () => clearInterval(retry)
  }, [dayOver, number])

  useEffect(() => {
    if (params.get('signin') === 'ok') {
      invalidate('me', 'play:', 'stats', 'board:', 'edchallenges:', 'mychallenges', 'challenge:', 'rivals')
      toast(<><Icon name="check" />You’re signed in. Your games are saved to your account.</>, 2600)
      // Signing in from a challenge's invite page comes back to it.
      const next = takeReturnAfterSignIn()
      if (next) navigate(next, { replace: true })
      else setParams({}, { replace: true })
    }
  }, [params, setParams, toast, navigate])

  if (ed.error) return <div className="page"><ErrorNote error={ed.error} retry={() => ed.reload()} /></div>
  if (!info) return <div className="page"><Loading /></div>

  const started = play && play.rows.length > 0
  const done = play?.phase === 'done'
  const todays = days.find((d) => d.edition === info.number)

  async function playNow() {
    // Playing accepts any invites waiting for you today.
    const invites = todays?.invites ?? []
    await Promise.all(invites.map((c) => api.post(`/challenges/${c.code}/join`).catch(() => {})))
    if (invites.length) invalidate(`edchallenges:${info!.number}`, 'mychallenges')
    navigate('/play')
  }

  return (
    <div className="page today">
      <header className="today-top">
        <Logo height={24} />
        <button type="button" className="ibtn today-help" aria-label="How to play" onClick={() => setHowto(true)}><Icon name="help" size={22} /></button>
        <Link to="/you" aria-label="You" className="today-av"><Avatar id={me?.id} name={me?.displayName} src={me?.avatar} size={32} /></Link>
      </header>

      <div className="masthead">
        <div className="double-rule" />
        <div className="eyebrow dateline"><span>No. {info.number}</span><span>{longDate(info.date)}</span></div>
        <div className="rule ink" />
      </div>

      <h1 className="h-serif today-headline"><Headline info={info} /></h1>

      <div className="card today-card">
        <div className="card-head"><span className="eyebrow ink-2">Today's gold</span><span className="eyebrow">boosts the multiplier</span></div>
        <div className="tiers wide">
          {goldByTier(info.gold).map(([tier, letters]) => (
            <div className="tier" key={tier}>
              <span className="tl">+{tier}</span>
              <div className="ts">{letters.map((l) => <Tile key={l} l={l} k="played gold" size={34} gb={false} gold={info.gold} rv={info.rulesVersion} />)}</div>
            </div>
          ))}
        </div>
        <div className="rule" />
        <Boards info={info} />
        {done && play ? (
          <Link className="today-done" to={`/results/${info.number}`}>
            <span className="today-rank"><i className="h-serif">{play.standing.name}</i><b>{fmt(play.scores.total)}</b></span>
            <span className="today-next">
              <span className="mono small faint">No. {info.number + 1} in <TimeLeft to={info.nextAt} /></span>
              <span className="today-see">See results<Icon name="next" size={16} /></span>
            </span>
          </Link>
        ) : (
          <button type="button" className="btn ink block today-go" onClick={playNow}>{started ? 'Continue' : 'Play'} No. {info.number}</button>
        )}
      </div>

      <TodayStrip days={days} today={info.number} />

      <SlowBurnCard />

      {me && (
        <Link to="/you" className="streak-card">
          <Icon name="streak" size={22} />
          <div>
            <b>{me.streak > 0 ? `${me.streak}-day streak` : 'Start a streak today'}</b>
            <span className="faint small ellipsis">{me.kind === 'guest' ? `Playing as ${me.displayName}` : me.displayName}</span>
          </div>
          <span className="chip">Your stats<Icon name="next" /></span>
        </Link>
      )}

      <footer className="today-foot faint small"><a href="/privacy">Privacy</a></footer>
      <TabBar />
      <HowTo open={howto} onClose={() => setHowto(false)} onPlay={() => navigate('/play')} />
    </div>
  )
}

/**
 * Today's Slow Burn, as option A of design/slow-burn/boards/TodayCard.html: your points and row,
 * a depth track against the day's deepest run, and the time left (to the next run once yours is
 * done, or to midnight while it's going, when it ends with the day).
 */
function SlowBurnCard() {
  const sb = useSlowBurn('today')
  const info = sb.data
  const run = useRun(info?.number ?? null).data
  if (!info || !run) return null
  const started = run.rows.length > 0
  return (
    <Link to="/slow-burn" className="sb-today">
      <div className="sb-today-top"><SlowBurnMark /><span className="eyebrow">Slow Burn · No. {info.number}</span></div>
      {run.ended ? (
        <div className="sb-today-done">
          <b className="h-serif">{fmt(run.scores.points)} points</b>
          <span className="mono small">row <b>{run.depth}</b>{run.placement ? <> · #{fmt(run.placement.position)} of {fmt(run.placement.players)}</> : null}</span>
        </div>
      ) : started ? (
        <div className="sb-today-done">
          <b className="h-serif">{fmt(run.scores.points)} points</b>
          <span className="mono small">on row <b>{run.rows.length + 1}</b> · so far</span>
        </div>
      ) : (
        <h2 className="h-serif">One letter burns <i>every</i> row.</h2>
      )}
      <DepthTrack you={run.ended ? run.depth : run.rows.length} deepest={info.deepest} now={run.ended || !started ? 0 : run.rows.length + 1} />
      <div className="sb-today-foot">
        {run.ended ? <span className="mono small sb-today-clock"><Icon name="clock" /><span>Next fuse in <TimeLeft to={info.nextAt} /></span></span>
          : started ? <span className="mono small sb-today-clock"><Icon name="clock" /><span><TimeLeft to={info.nextAt} /> to finish</span></span>
          : <span className="mono small">{info.players ? `${fmt(info.players)} run${info.players === 1 ? '' : 's'} so far` : 'Be the first to finish'}</span>}
        <span className="btn gold sm">{run.ended ? 'See results' : started ? 'Continue' : 'Play now'}<Icon name="next" /></span>
      </div>
    </Link>
  )
}

/** "6h 41m" (or "12m") until an ISO instant, updated each minute. */
function TimeLeft({ to }: { to: string }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [])
  const mins = Math.max(0, Math.ceil((Date.parse(to) - Date.now()) / 60_000))
  return <span className="when">{mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`}</span>
}

/**
 * Today's board, with its burn tiles, beside yesterday's best: the solver's board, its total and
 * the day's #1. Until yesterday's edition loads (or on No. 0) today's board stands alone.
 */
function Boards({ info }: { info: EditionInfo }) {
  const m = useIsDesktop() ? 18 : 14
  return (
    <div className="today-boards">
      <div className="today-half">
        <span className="eyebrow ink-2">Today</span>
        <div className="burnmap" style={{ '--m': `${m}px` } as CSSProperties} aria-label="Where today's burn tiles are">
          {Array.from({ length: 30 }, (_, i) => <i key={i} className={isBurn(info, Math.floor(i / 5), i % 5) ? 'b' : ''} />)}
        </div>
      </div>
      {info.number > 0 && <Yesterday n={info.number - 1} m={m} />}
    </div>
  )
}

/** Yesterday's half of the card, which opens that edition's results at Best words. */
function Yesterday({ n, m }: { n: number; m: number }) {
  // Asked for once you're known: the API shows a past edition's best words only to a player.
  const me = useMe().data
  const info = useEdition(me ? n : null).data
  const solver = info?.bestLine
  if (!info || !solver) return null
  const top = info.topLine
  const you = top?.playerId === me?.id
  const label = `Yesterday’s best words, No. ${n}: the solver made ${fmt(solver.total)}, ${
    !top ? 'nobody finished it' : you ? `you were #1 with ${fmt(top.total)}` : `#1 was ${top.displayName} with ${fmt(top.total)}`}`
  return (
    <>
      <i className="today-sep" />
      <Link to={`/results/${n}#best`} className="today-half" aria-label={label}>
        <span className="eyebrow"><span className="ink-2">Yesterday</span><Icon name="next" size={14} /></span>
        <div className="yday">
          <MiniGrid edition={info} rows={solver.rows} m={m} />
          <div className="yday-lines">
            <div><span>Solver</span><b>{fmt(solver.total)}</b></div>
            {!top ? <div><span>#1</span><span className="nobody">Nobody finished</span></div>
              : you ? <div className="you"><span>#1 · You</span><b>{fmt(top.total)}</b></div>
              : <div><span>#1 {top.displayName}</span><b>{fmt(top.total)}</b></div>}
          </div>
        </div>
      </Link>
    </>
  )
}

/**
 * The edition's line, with its emphasis in italic burn red. The +3 letters ("H and K") never break
 * apart, and neither does a one-word emphasis such as "white-hot" (longer ones may wrap).
 */
function Headline({ info }: { info: EditionInfo }) {
  const { headline: text, headlineEmphasis: emphasis } = info
  const letters = headlineLetters(info.gold)
  const keep = (s: string) => (letters.includes(' ') ? s.split(letters).join(letters.replaceAll(' ', '\u00a0')) : s)
  const at = emphasis ? text.indexOf(emphasis) : -1
  if (!emphasis || at < 0) return <>{keep(text)}</>
  return (
    <>
      {keep(text.slice(0, at))}
      <em className={`burn-em${emphasis.includes(' ') ? '' : ' nowrap'}`}>{emphasis}</em>
      {keep(text.slice(at + emphasis.length))}
    </>
  )
}
